import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface ExportDropdownProps {
  onExport: (format: "xlsx" | "csv") => void;
  isExporting?: boolean;
  disabled?: boolean;
  variant?: "primary" | "outline";
  align?: "left" | "right";
  className?: string;
}

export const ExportDropdown: React.FC<ExportDropdownProps> = ({
  onExport,
  isExporting = false,
  disabled = false,
  variant = "primary",
  align = "right",
  className = "",
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (format: "xlsx" | "csv") => {
    setIsOpen(false);
    onExport(format);
  };

  const isOutline = variant === "outline";

  return (
    <div className={`relative inline-block text-left ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && !isExporting && setIsOpen((prev) => !prev)}
        disabled={disabled || isExporting}
        className={`inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
          isOutline
            ? "bg-white hover:bg-gray-50 active:bg-gray-100 border border-gray-200 text-gray-700 shadow-2xs hover:shadow-xs"
            : "bg-[#041B44] hover:bg-[#031535] text-white shadow-2xs hover:shadow-xs"
        }`}
        title={t("common.exportOptions", "Export data")}
      >
        {isExporting ? (
          <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin shrink-0" />
        ) : (
          <svg
            className={`w-4 h-4 shrink-0 ${isOutline ? "text-gray-600" : "text-white"}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        )}
        <span>{isExporting ? t("common.exporting", "Exporting...") : t("common.export", "Export")}</span>
        <svg
          className={`w-3.5 h-3.5 ml-0.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          className={`absolute z-50 mt-1.5 w-56 rounded-xl bg-white shadow-xl border border-gray-100 py-1.5 ring-1 ring-black/5 animate-in fade-in zoom-in-95 duration-100 ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          {/* Option: Excel (.xlsx) */}
          <button
            type="button"
            onClick={() => handleSelect("xlsx")}
            className="w-full flex items-center justify-between px-3.5 py-2.5 text-xs sm:text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-900 transition-colors text-left cursor-pointer group"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="p-1 rounded-md bg-emerald-100/70 text-emerald-700 group-hover:bg-emerald-200/80 transition-colors shrink-0">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </span>
              <div className="flex flex-col min-w-0">
                <span className="font-semibold text-gray-800 group-hover:text-emerald-900 truncate">
                  {t("common.exportExcel", "Export Excel")}
                </span>
                <span className="text-[11px] text-gray-400 group-hover:text-emerald-700/80">.xlsx format</span>
              </div>
            </div>
            <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-100 text-emerald-800 rounded-full shrink-0">
              {t("common.recommended", "Best")}
            </span>
          </button>

          <div className="my-1 border-t border-gray-100" />

          {/* Option: CSV (.csv) */}
          <button
            type="button"
            onClick={() => handleSelect("csv")}
            className="w-full flex items-center justify-between px-3.5 py-2.5 text-xs sm:text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors text-left cursor-pointer group"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="p-1 rounded-md bg-blue-100/70 text-blue-700 group-hover:bg-blue-200/80 transition-colors shrink-0">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </span>
              <div className="flex flex-col min-w-0">
                <span className="font-medium text-gray-700 group-hover:text-blue-900 truncate">
                  {t("common.exportCsv", "Export CSV")}
                </span>
                <span className="text-[11px] text-gray-400 group-hover:text-blue-700/80">.csv format</span>
              </div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportDropdown;

